// The role-scoped sidebar nav, recreated 1:1 from the prototype (brand/ritona-app-handoff app.js
// renderSidebar + app.css .sidebar/.brand/.org/.nav/.nav-item/.user). Top-to-bottom: the Ritona brand
// lockup, the org block, the grouped nav (Work / Knowledge / Operate — the Operate group is operator-only),
// and the account/settings button pinned at the bottom. Styled by the ported handoff stylesheet (tokens
// only, G-181).
//
// Selecting an item changes the active surface client-side (no route change) — the chat surface stays
// reachable throughout (N-CHAT-1). SCOPING NOTE (G-180, N-FDE-2 AC-6): this list decides what the NAV
// renders; it is NOT the security boundary — every surface's data is authorised by its owning read interface
// server-side.

import { NAV_GROUPS, SETTINGS_SURFACE, roleCanSeeSurface, surfacesForRole } from './surfaces.ts';
import type { DashboardRole, SurfaceId } from './surfaces.ts';
import { SURFACE_ICONS } from './surface-icons.ts';
import { Icon } from './Icon.tsx';
import { Avatar } from './Avatar.tsx';
import { RitonaLockup } from './Brand.tsx';
import { useViewerIdentity } from './viewer-identity.tsx';
import { useOrgDisplay } from './org-display.tsx';

export interface SidebarProps {
  readonly role: DashboardRole;
  readonly active: SurfaceId;
  readonly onSelect: (id: SurfaceId) => void;
}

/** One nav item button (prototype .nav-item): icon, label, current-page marking. */
function NavButton({
  id,
  label,
  isActive,
  onSelect,
}: {
  readonly id: SurfaceId;
  readonly label: string;
  readonly isActive: boolean;
  readonly onSelect: (id: SurfaceId) => void;
}): React.JSX.Element {
  return (
    <li>
      <button
        type="button"
        aria-current={isActive ? 'page' : undefined}
        onClick={() => onSelect(id)}
        className={isActive ? 'nav-item active' : 'nav-item'}
      >
        <span className="ico">
          <Icon name={SURFACE_ICONS[id]} />
        </span>
        <span className="label">{label}</span>
      </button>
    </li>
  );
}

/** The account identity block pinned at the sidebar foot (prototype .user). It opens Settings — but ONLY for
 *  a role that can reach the Settings surface. Settings is operator-only (the owning read is
 *  `requiredRole:'operator'`), so a member's account block renders as a plain, non-interactive identity panel:
 *  the sidebar never dangles a Settings affordance the owning read would 403 (role-honesty, G-180). */
function AccountButton({ role, active, onSelect }: SidebarProps): React.JSX.Element {
  const viewer = useViewerIdentity();
  const name = viewer?.name ?? 'Signed-in user';
  const initials = viewer?.initials ?? '?';
  const roleLabel = viewer?.roleLabel ?? 'Identity unavailable';
  const identity = (
    <>
      <Avatar initials={initials} size="lg" />
      <span className="who">
        <span className="nm">{name}</span>
        <span className="rl">{roleLabel}</span>
      </span>
    </>
  );
  // A role without Settings sees identity only — no gear, no navigation (the affordance would only 403).
  if (!roleCanSeeSurface(role, SETTINGS_SURFACE.id)) {
    return (
      <div className="user" aria-label="Account">
        {identity}
      </div>
    );
  }
  const isActive = active === SETTINGS_SURFACE.id;
  return (
    <button
      type="button"
      aria-current={isActive ? 'page' : undefined}
      aria-label="Account and settings"
      onClick={() => onSelect(SETTINGS_SURFACE.id)}
      className={isActive ? 'user active' : 'user'}
    >
      {identity}
      <span className="gear">
        <Icon name="settings" size={16} />
      </span>
    </button>
  );
}

/** The sidebar: brand, org, each non-empty nav group for the role, then the account button. */
export function Sidebar({ role, active, onSelect }: SidebarProps): React.JSX.Element {
  const surfaces = surfacesForRole(role);
  // Per-DEPLOYMENT, not per-release: the org block reads this box's configured identity (owner direction,
  // 7 Aug 2026). The hook always answers — the built-in default until the shell's read lands — so this
  // block never paints empty and an un-reconfigured box looks exactly as it did before.
  const org = useOrgDisplay();
  return (
    <aside className="sidebar">
      <div className="brand">
        <RitonaLockup />
      </div>
      <div className="org">
        <div className="name">{org.name}</div>
        <div className="meta">{org.meta}</div>
      </div>
      <nav aria-label="Primary" className="nav">
        {NAV_GROUPS.map((group) => {
          const inGroup = surfaces.filter((surface) => surface.group === group);
          if (inGroup.length === 0) return null;
          return (
            <div key={group}>
              <div className="nav-group">{group}</div>
              <ul className="m-0 list-none p-0">
                {inGroup.map((surface) => (
                  <NavButton
                    key={surface.id}
                    id={surface.id}
                    label={surface.label}
                    isActive={surface.id === active}
                    onSelect={onSelect}
                  />
                ))}
              </ul>
            </div>
          );
        })}
      </nav>
      <AccountButton role={role} active={active} onSelect={onSelect} />
    </aside>
  );
}
